import { createEntityAdapter, createSelector, EntityState } from '@reduxjs/toolkit';
import { Vehicle, VehicleFetchingType, VehicleType } from 'src/@types/vehicle';
import { apiSlice } from './apiSlice';
import { RootState } from '../store';
import { getApplicationTimestamp } from '../../utils/applicationTimestamp';
import { serializeLocalDateFields } from '../../utils/localDate';

const vehicleAdapter = createEntityAdapter<Vehicle>();
const initialState = vehicleAdapter.getInitialState();

export const vehicleApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getVehiclesByFetchingType: builder.query<EntityState<Vehicle, string>, VehicleFetchingType>({
      query: (vehicleFetchingType) =>
        `/vehicles/all/${getApplicationTimestamp()}/${vehicleFetchingType}`,
      transformResponse: (response: Vehicle[]) => vehicleAdapter.setAll(initialState, response),
      providesTags: ['Vehicle'],
    }),
    getAllVehicleTypes: builder.query<VehicleType[], void>({
      query: () => `/vehicles/vehicle-types`,
      providesTags: ['Vehicle'],
    }),
    updateVehicle: builder.mutation({
      query: ({ id, vehicle }) => ({
        url: `/vehicles/${id}`,
        method: 'PUT',
        body: serializeLocalDateFields(vehicle, [
          'endOfAdmission',
          'vehicleRegistrationDate',
          'unavailableFrom',
        ]),
      }),
      invalidatesTags: ['Vehicle', 'Place'],
    }),
    createVehicle: builder.mutation({
      query: ({ vehicle }) => ({
        url: '/vehicles',
        method: 'POST',
        body: serializeLocalDateFields(vehicle, [
          'endOfAdmission',
          'vehicleRegistrationDate',
          'unavailableFrom',
        ]),
      }),
      invalidatesTags: ['Vehicle'],
    }),
    deleteVehicle: builder.mutation({
      query: ({ id }) => ({
        url: `/vehicles/${id}`,
        method: 'POST',
      }),
      invalidatesTags: ['Vehicle'],
    }),
    deleteVehicles: builder.mutation({
      query: ({ ids }) => ({
        url: `/vehicles`,
        method: 'DELETE',
        body: { ids },
      }),
      invalidatesTags: ['Vehicle'],
    }),
  }),
});

export const {
  useGetVehiclesByFetchingTypeQuery,
  useUpdateVehicleMutation,
  useCreateVehicleMutation,
  useGetAllVehicleTypesQuery,
  useDeleteVehicleMutation,
  useDeleteVehiclesMutation,
} = vehicleApiSlice;

export const selectVehicleResult = vehicleApiSlice.endpoints.getVehiclesByFetchingType.select(
  VehicleFetchingType.WITHOUT_RESPECTING_UNAVAILABLE_FROM_DATE
);
const selectVehicleData = createSelector(
  selectVehicleResult,
  (VehicleResult) => VehicleResult.data
);

export const { selectAll: selectAllVehicles, selectById: selectVehicleById } =
  vehicleAdapter.getSelectors<RootState>((state) => selectVehicleData(state) ?? initialState);

export const useGetAllVehiclesByFetchingType = (vehicleFetchingType: VehicleFetchingType) => {
  const { data, isSuccess } = useGetVehiclesByFetchingTypeQuery(vehicleFetchingType);
  let vehicles: Vehicle[] = [];
  if (isSuccess) {
    vehicles = fillVehicleList(data);
  }
  return vehicles;
};

const fillVehicleList = (vehicleData: EntityState<Vehicle, string>) => {
  const vehicleList: Vehicle[] = [];
  Object.values(vehicleData.entities).forEach((elem) => vehicleList.push(elem));
  return vehicleList;
};
